import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a paginated list of items.
 *
 * Paginas seguintes so sao buscadas quando o sentinel (viewMoreNext/viewMorePrevious)
 * entra na janela de 600px do viewport, ou pelo botao "Ver mais modelos" — nunca no
 * load (auditoria 15/09: a colecao buscava page=2 sem scroll e a busca, pages 2 e 3).
 *
 * Modo agrupado (data-r9-grouped, colecoes e busca): a Shopify pagina antes de
 * agrupar, entao a mesma familia+acabamento pode aparecer em paginas diferentes.
 * A identidade e a mesma do Liquid (snippets/r9-product-group-key); a deduplicacao
 * entre paginas e incremental: cada pagina nativa buscada sob demanda tem suas
 * chaves comparadas com as ja exibidas, na ordem da Shopify (primeira ocorrencia
 * vence). A contagem de modelos so e definitiva quando a ultima pagina foi lida;
 * antes disso o status mostra "N modelos carregados".
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** Distancia (px) antes do sentinel entrar na tela para comecar a buscar a proxima pagina. */
  static SENTINEL_ROOT_MARGIN = '600px 0px';

  #pageEdges = { next: null, previous: null };
  #rendering = new Set();
  #generation = 0;

  // modo agrupado
  /** @type {Set<string>} chaves de identidade ja exibidas na grade */
  #groupKeys = new Set();
  #groupNextPage = 2;
  #groupLastPage = 1;
  #groupLoading = false;
  #groupDone = false;

  /** @type {string | null} */
  #imageRatioSetting = null;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();

    if (this.hasAttribute('data-r9-grouped')) this.#initGroups();
    else this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.addEventListener('r9:pagination-updated', this.#handlePaginationUpdated);
  }

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.removeEventListener('r9:pagination-updated', this.#handlePaginationUpdated);
    this.#generation++;
  }

  /** @returns {{ grid: HTMLElement, button: HTMLButtonElement, status: HTMLElement } | null} */
  #groupControls() {
    const { grid } = this.refs;
    const controls = this.querySelector('[data-r9-group-controls]');
    const button = controls?.querySelector('[data-r9-group-more]');
    const status = controls?.querySelector('[data-r9-group-status]');
    if (!grid || !controls || !(button instanceof HTMLButtonElement) || !status) return null;
    controls.hidden = false;
    return { grid, button, status };
  }

  /**
   * @param {Element} card
   * @returns {string | null}
   */
  #groupKey(card) {
    return card.getAttribute('data-r9-group-key') || card.getAttribute('data-product-id');
  }

  /**
   * Modo agrupado: a pagina renderizada pelo servidor (ja deduplicada dentro dela
   * mesma pelo Liquid) e o ponto de partida; nada e buscado aqui.
   */
  #initGroups() {
    const ctl = this.#groupControls();
    if (!ctl) return;
    const { grid, button } = ctl;
    const currentPage = Number(grid.dataset.currentPage || 1);
    this.#groupLastPage = Math.max(1, Number(grid.getAttribute('last-page') || 1));
    this.#groupKeys = new Set();
    for (const card of grid.children) {
      const key = this.#groupKey(card);
      if (key) this.#groupKeys.add(key);
    }
    this.#groupNextPage = currentPage + 1;
    this.#groupDone = this.#groupNextPage > this.#groupLastPage;
    this.#groupLoading = false;
    button.disabled = false;
    button.textContent = button.dataset.more;
    button.hidden = this.#groupDone;
    button.onclick = () => this.#loadNextGroupPage();
    this.#updateGroupStatus();
    this.#observeGroupSentinel();
  }

  #observeGroupSentinel() {
    this.infinityScrollObserver?.disconnect();
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext || this.#groupDone || typeof IntersectionObserver === 'undefined') return;
    this.infinityScrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#loadNextGroupPage();
      },
      { rootMargin: PaginatedList.SENTINEL_ROOT_MARGIN }
    );
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  #updateGroupStatus() {
    const ctl = this.#groupControls();
    if (!ctl) return;
    const { grid, button, status } = ctl;
    const shown = String(grid.children.length);
    const label = this.#groupDone
      ? (button.dataset.count || '').replace('{shown}', shown).replace('{total}', shown)
      : (button.dataset.loaded || button.dataset.count || '').replace('{shown}', shown).replace('{total}', '…');
    status.textContent = label;
    this.querySelectorAll('[data-r9-group-count]').forEach((element) => {
      element.textContent = label;
    });
  }

  /**
   * Busca a proxima pagina nativa da MESMA consulta (filtros/ordenacao preservados na
   * URL), deduplica contra as chaves ja exibidas e anexa so os modelos novos. Uma pagina
   * cujas chaves ja apareceram todas nao para a leitura: segue para a seguinte, ate
   * achar modelo novo ou esgotar as paginas.
   */
  async #loadNextGroupPage() {
    if (this.#groupLoading || this.#groupDone) return;
    const ctl = this.#groupControls();
    if (!ctl) return;
    const { grid, button, status } = ctl;
    const generation = this.#generation;
    const isCurrent = () => generation === this.#generation && this.isConnected && grid === this.refs.grid;
    this.#groupLoading = true;
    this.infinityScrollObserver?.disconnect();
    button.disabled = true;
    button.textContent = button.dataset.loading || '';
    status.textContent = button.dataset.loading || '';
    grid.setAttribute('aria-busy', 'true');
    let page = this.#groupNextPage;
    try {
      while (page <= this.#groupLastPage) {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.set('page', String(page));
        await this.#fetchSpecificPage(page, url);
        if (!isCurrent()) return;
        const cards = this.#getGridForPage(page);
        if (!cards) throw new Error(`Missing result grid on page ${page}`);
        const additions = [];
        for (const card of cards) {
          const key = this.#groupKey(card);
          if (!key) throw new Error(`Missing product identity on page ${page}`);
          if (this.#groupKeys.has(key)) continue;
          this.#groupKeys.add(key);
          // Parsed HTML has no listeners; let the existing swatch observer bind again.
          card.querySelectorAll('[data-r9-init]').forEach((rail) => rail.removeAttribute('data-r9-init'));
          additions.push(card);
        }
        page++;
        this.#groupNextPage = page;
        this.#groupDone = page > this.#groupLastPage;
        if (additions.length) {
          grid.append(...additions);
          this.#processNewElements();
          break;
        }
      }
      button.textContent = button.dataset.more || '';
      button.disabled = false;
      button.hidden = this.#groupDone;
      this.#updateGroupStatus();
      this.#groupLoading = false;
      this.#observeGroupSentinel();
    } catch (error) {
      if (!isCurrent()) return;
      console.error('R9 grouped results failed', this.sectionId, page, error);
      status.textContent = button.dataset.error || '';
      button.textContent = button.dataset.retry || '';
      button.disabled = false;
      button.hidden = false;
      this.#groupLoading = false;
    } finally {
      if (isCurrent()) grid.removeAttribute('aria-busy');
    }
  }

  #observeViewMore() {
    this.infinityScrollObserver?.disconnect();
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious || !viewMoreNext || typeof IntersectionObserver === 'undefined') return;

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        // Wait for any in-progress view transitions to finish
        if (viewTransition.current) await viewTransition.current;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (entry.target === viewMorePrevious) {
              this.#renderPreviousPage();
            } else {
              this.#renderNextPage();
            }
          }
        }
      },
      {
        rootMargin: PaginatedList.SENTINEL_ROOT_MARGIN,
      }
    );

    this.infinityScrollObserver.observe(viewMorePrevious);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * @param {Object} pageInfo - The page info
   * @param {number} pageInfo.page - The page number
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page < 1 || pageInfo.page > Number(this.refs.grid?.getAttribute('last-page') || 1)) return false;

    return true;
  }

  async #fetchSpecificPage(pageNumber, url) {
    const generation = this.#generation;
    if (this.pages.has(pageNumber)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, new URL(url));
    if (generation === this.#generation && this.isConnected) this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() { await this.#renderPage('next'); }
  async #renderPreviousPage() { await this.#renderPage('previous'); }

  async #renderPage(type) {
    if (this.#rendering.has(type)) return;
    const generation = this.#generation;
    const { grid } = this.refs;
    if (!grid) return;
    this.#rendering.add(type);
    try {
      let page = this.#getPage(type);
      while (page && this.#shouldUsePage(page)) {
        await this.#fetchSpecificPage(page.page, page.url);
        if (generation !== this.#generation || !this.isConnected || grid !== this.refs.grid) return;
        const elements = this.#getGridForPage(page.page);
        if (!elements) throw new Error('Pagination response has no grid');
        this.#pageEdges[type] = page.page;
        if (!elements.length) {
          page = this.#getPage(type);
          continue;
        }
        const firstElement = grid.firstElementChild;
        const scrollTop = window.scrollY;
        const oldTop = firstElement?.getBoundingClientRect().top ?? 0;
        if (type === 'previous') grid.prepend(...elements);
        else grid.append(...elements);
        this.#processNewElements();
        history.pushState('', '', page.url.toString());
        if (type === 'previous' && firstElement) {
          window.scrollTo({ top: scrollTop + firstElement.getBoundingClientRect().top - oldTop, behavior: 'instant' });
        }
        requestIdleCallback(() => {
          if (generation !== this.#generation || !this.isConnected) return;
          this.#observeViewMore();
        });
        return;
      }
    } catch (error) {
      console.error('Pagination render failed', this.sectionId, type, error);
    } finally {
      if (generation === this.#generation) this.#rendering.delete(type);
    }
  }

  /**
   * Process newly added elements and apply correct aspect ratios
   */
  #processNewElements() {
    // Wait for the DOM to update
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   * For the 'adapt' setting, each product should use its own image's aspect ratio
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   * Only used for non-adaptive modes (square, portrait, landscape)
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    // Batch DOM operations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const cards = this.querySelectorAll('[ref="grid"] > [ref="cards[]"]');
    const isPrevious = type === 'previous';
    const targetCard = cards[isPrevious ? 0 : cards.length - 1];
    const current = this.#pageEdges[type] ?? Number(targetCard?.getAttribute('data-page') || this.refs.grid?.getAttribute('data-current-page') || new URL(location.href).searchParams.get('page') || 1);
    const page = current + (isPrevious ? -1 : 1);

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#generation++;
    this.pages.clear();
    this.#pageEdges = { next: null, previous: null };
    this.#rendering.clear();
    this.#groupKeys = new Set();
    this.#groupLoading = false;
    this.infinityScrollObserver?.disconnect();
  };

  #handlePaginationUpdated = (event) => {
    if (event.detail.sectionId !== this.sectionId || !this.isConnected) return;
    this.#storeImageRatioSettings();
    if (this.hasAttribute('data-r9-grouped')) {
      this.#initGroups();
      return;
    }
    this.#observeViewMore();
  };
}
